import * as _ from 'lodash';

import * as Graph from '../graph';
import * as CGTypes from '../types';
import * as Types from '../model/types';
import * as HL from '../hl';
import {
  constString,
  opArray,
  opArtifactVersionFile,
  opConcat,
  opFileArtifactVersion,
  opFileDir,
  opFileTable,
  opTableRows,
} from '../ops';
import * as OpTypes from '../opTypes';
import {Client} from '../client';
import * as MediaTable from '../model/mediaTable';
import {EngineClient} from '../client/engineClient';

const opPartitionedTableInputTypes = {
  partitionedTable: Types.maybe({
    type: 'partitioned-table' as const,
    columnTypes: {},
  }),
};

const partitionedTableExpansionInputTypes = {
  partitionedTable: {type: 'partitioned-table' as const, columnTypes: {}},
};

const partitionedTableExpansion = async (
  client: Client,
  partitionedTable: MediaTable.MediaPartitionedTable,
  inputs: CGTypes.OpInputNodes<typeof partitionedTableExpansionInputTypes>
): Promise<Types.OutputNode> => {
  const inputFileNode = opPartitionedTableFile({
    partitionedTable: inputs.partitionedTable,
  });
  const upstreamArtifactVersionNode = opFileArtifactVersion({
    file: inputFileNode,
  });
  const partsPath = partitionedTable.parts_path;
  const fileDirNode = opFileDir({
    file: opArtifactVersionFile({
      artifactVersion: upstreamArtifactVersionNode,
      path: constString(partsPath),
    }) as any,
  });
  // Augh, TODO: implement this as op! A dir should be an array of file
  // probably.
  const fileDir = await (client as Client).query(fileDirNode);
  const partTables = fileDir.files;
  const tableNodes = Object.keys(partTables)
    .filter(key => key.endsWith('table.json'))
    .map(key => {
      return opFileTable({
        file: opArtifactVersionFile({
          artifactVersion: upstreamArtifactVersionNode,
          path: constString(partTables[key].fullPath),
        }) as any,
      });
    });

  // TODO: Assert homogenous column types
  const tableRowNodes = tableNodes.map(node => {
    return opTableRows({table: node as any});
  });
  const tableRowArrayNode = opArray(
    _.fromPairs(tableRowNodes.map((node, ndx) => [ndx, node])) as any
  );
  return opConcat({
    arr: tableRowArrayNode as any,
  });
};

export const opPartitionedTableRows = Graph.makeOp({
  name: 'partitionedtable-rows',
  argTypes: opPartitionedTableInputTypes,
  description: `Returns the rows of a ${Graph.docType('partitioned-table')}`,
  argDescriptions: {
    partitionedTable: `The ${Graph.docType(
      'partitioned-table'
    )} to get rows from`,
  },
  returnValueDescription: `Rows of the ${Graph.docType('partitioned-table')}`,
  returnType: inputs => {
    return OpTypes.mappableNullableSkipTaggable(
      inputs.partitionedTable.type,
      t => {
        if (!Types.isPartitionedTable(t)) {
          throw new Error('invalid');
        }
        return Types.list(
          Types.withTableRowTag(
            Types.typedDict({}),
            inputs.partitionedTable.type
          )
        );
      }
    );
  },
  resolver: async (inputs, forwardGraph, forwardOp, context, engine) => {
    const localEngine = engine();
    return OpTypes.mappableNullableSkipTaggableValAsync(
      inputs.partitionedTable,
      async (pt, ptWithTags) => {
        const joined2 = await partitionedTableExpansion(
          new EngineClient(localEngine),
          pt.partitionedTable,
          forwardOp.outputNode.node.fromOp.inputs as any
        );

        // TODO: get rid of await
        return (await localEngine.executeNodes([joined2], false))[0];
      }
    );
  },
  resolveOutputType: async (node, executableNode, client) => {
    const inputPartitionedTableNode =
      executableNode.fromOp.inputs.partitionedTable;
    if (inputPartitionedTableNode.nodeType !== 'output') {
      throw new Error('invalid');
    }
    const pt = await client.query(inputPartitionedTableNode);

    if (pt == null) {
      return {
        ...node,
        type: 'none',
      };
    }
    const joined2 = await partitionedTableExpansion(
      client,
      pt.partitionedTable,
      executableNode.fromOp.inputs as any
    );
    const joinedRefined = await HL.refineNode(client, joined2, {});
    return {
      ...node,
      type: joinedRefined.type,
    };
  },
});

export const opPartitionedTableFile = Graph.makeOp({
  name: 'partitionedtable-file',
  argTypes: opPartitionedTableInputTypes,
  description: `Returns the ${Graph.docType('file')} of a ${Graph.docType(
    'partitioned-table'
  )}`,
  argDescriptions: {
    partitionedTable: `The ${Graph.docType('partitioned-table')}`,
  },
  returnValueDescription: `${Graph.docType('file')} of the ${Graph.docType(
    'partitioned-table'
  )}`,
  returnType: inputs =>
    OpTypes.mappableNullableTaggable(inputs.partitionedTable.type, t => {
      return {type: 'file'};
    }),
  resolver: async inputs => {
    return await OpTypes.mappableNullableTaggableVal(
      inputs.partitionedTable,
      partitionedTable => {
        return partitionedTable;
      }
    );
  },
});
